import React, { useRef, useEffect, useState } from 'react';
import * as THREE from 'three';
import { OrbitControls } from '@react-three/drei';
import { Canvas, useThree } from '@react-three/fiber';
import { GCodeParser, createGCodeGeometry } from '../utils/gcodeParser';

interface GCodeVisualizerProps {
  gcodeContent: string;
  width?: number | string;
  height?: number | string;
  bedDimensions?: { width: number; height: number; source?: string };
}

function GCodeToolPaths({ gcodeContent, onLayerInfoUpdate, onSceneRef, bedDimensions }: { gcodeContent: string, onLayerInfoUpdate: (layerCount: number) => void, onSceneRef: (scene: THREE.Scene) => void, bedDimensions?: { width: number; height: number; source?: string } }) {
  const extrudeRef = useRef<THREE.LineSegments>(null);
  const travelRef = useRef<THREE.LineSegments>(null);
  const [loaded, setLoaded] = useState(false);
  const { camera, scene } = useThree();
  const buildPlateSize = 250;
  const centerX = buildPlateSize/2;
  const centerY = buildPlateSize/2;
  const parserRef = useRef(new GCodeParser());
  
  // One-time setup of scene
  useEffect(() => {
    // Clear everything from the scene that might have been added
    while(scene.children.length > 0) { 
      const object = scene.children[0];
      if (object instanceof THREE.Object3D) {
        scene.remove(object);
      }
    }
    
    // Set camera up vector for correct orbit orientation
    camera.up.set(0, 0, 1);
    
    // Create a new scene from scratch
    setupScene();
  }, [scene, camera]);
  
  // Setup the scene objects once
  const setupScene = (initialBedSizeX?: number | null, initialBedSizeY?: number | null) => {
    // Remove old grid if it exists
    const oldGrid = scene.getObjectByName("buildPlateGrid");
    if (oldGrid) {
        scene.remove(oldGrid);
        // Consider disposing geometry/material if needed, but often handled by GC
    }
    
    // Only create grid if we have valid dimensions
    if (initialBedSizeX && initialBedSizeY) {
      const sizeX = initialBedSizeX;
      const sizeY = initialBedSizeY;
      const gridSize = Math.max(sizeX, sizeY); // Use the larger dimension for grid size
      const gridDivisions = Math.ceil(gridSize / 10); // Ensure 10mm squares
      const finalGridSize = gridDivisions * 10; // Make sure size is multiple of 10
      
      // Create grid helper for print bed - positioned with corner at origin
      const grid = new THREE.GridHelper(finalGridSize, gridDivisions, 0x444444, 0x888888);
      // Rotate the grid for X-Y plane (rotate 90 degrees around X axis)
      grid.rotation.x = Math.PI / 2;
      grid.name = "buildPlateGrid";
      
      // Position the grid with corner near origin (adjusting for GridHelper center)
      grid.position.set(finalGridSize / 2, finalGridSize / 2, 0);
      scene.add(grid);
    } else {
      console.log(`[GCODE VISUALIZER DEBUG] ℹ️ No bed dimensions provided - skipping grid creation`);
    }
    
    // Add axes helper at origin (fixed)
    const oldAxes = scene.getObjectByName("axesHelper");
    if (!oldAxes) { // Only add if not already present
      const axesHelper = new THREE.AxesHelper(50); // Keep axes size fixed for now
      axesHelper.name = "axesHelper";
      axesHelper.position.set(0, 0, 0); // Fixed at origin
      scene.add(axesHelper);
    }
  };
  
  // Handle G-code changes
  useEffect(() => {
    if (!gcodeContent) return;
    
    try {
      // Find existing toolpaths in scene and remove them
      scene.children.forEach(child => {
        if (child.name === "extrudePaths" || child.name.startsWith("layer_") || child.name === "travelPaths") {
          scene.remove(child);
        }
      });
      
      // Parse GCode using the ref
      const parser = parserRef.current;
      const toolPaths = parser.parse(gcodeContent);
      
      // Use provided bed dimensions or parse from G-code as fallback
      let finalBedDimensions = { x: null as number | null, y: null as number | null };
      
      console.log(`[GCODE VISUALIZER DEBUG] Received bedDimensions prop:`, bedDimensions);
      
      if (bedDimensions) {
        finalBedDimensions.x = bedDimensions.width;
        finalBedDimensions.y = bedDimensions.height;
        console.log(`[GCODE VISUALIZER DEBUG] ✅ Using provided bed dimensions (${bedDimensions.source}): ${bedDimensions.width}x${bedDimensions.height}`);
      } else {
        console.log(`[GCODE VISUALIZER DEBUG] No bed dimensions provided, trying to parse from G-code`);
        // Fallback to parsing from G-code
        const parsedDimensions = parser.getBedDimensions();
        finalBedDimensions = parsedDimensions;
        console.log(`[GCODE VISUALIZER DEBUG] Parser returned dimensions:`, parsedDimensions);
        if (parsedDimensions.x && parsedDimensions.y) {
          console.log(`[GCODE VISUALIZER DEBUG] ✅ Using parsed bed dimensions from G-code: ${parsedDimensions.x}x${parsedDimensions.y}`);
        } else {
          console.log(`[GCODE VISUALIZER DEBUG] ℹ️ No valid dimensions found - no build plate grid will be shown`);
          finalBedDimensions.x = null;
          finalBedDimensions.y = null;
        }
      }
      
      // --- Update Grid if dimensions were found ---
      if (finalBedDimensions.x !== null && finalBedDimensions.y !== null) {
          console.log(`[GCODE VISUALIZER DEBUG] ✅ Setting up scene with bed dimensions: ${finalBedDimensions.x}x${finalBedDimensions.y}`);
          setupScene(finalBedDimensions.x, finalBedDimensions.y); // Re-setup scene with new dimensions
      } else {
          console.log(`[GCODE VISUALIZER DEBUG] ℹ️ No bed dimensions available - setting up scene without build plate grid`);
          // Setup scene without grid (no bed dimensions)
          setupScene(null, null);
      }
      // --- End Grid Update ---
      
      // Get bounds for camera setup
      const bounds = parser.getBounds();
      
      // Calculate size for proper camera distance
      const size = new THREE.Vector3()
        .subVectors(bounds.max, bounds.min);
      
      // Initial camera setup - only adjust camera if it's the first load
      if (!loaded) {
        // Get max dimension for camera distance
        const maxDim = Math.max(size.x, size.y, size.z);
        const distance = Math.max(maxDim * 2, 200); // Increased distance for better view
        
        // Update camera position while maintaining angle
        const currentDirection = new THREE.Vector3().subVectors(
          camera.position,
          new THREE.Vector3(centerX, centerY, 0)
        ).normalize();
        
        camera.position.copy(
          new THREE.Vector3(centerX, centerY, 0).add(
            currentDirection.multiplyScalar(distance)
          )
        );
        
        // Always look at build plate center
        camera.lookAt(centerX, centerY, 0);
        
        // Update OrbitControls
        const controls = scene.userData.orbitControls;
        if (controls) {
          controls.target.set(centerX, centerY, 0);
          controls.update();
        }
        
        // Mark as loaded so we don't reset camera on subsequent updates
        setLoaded(true);
      }
      
      // Calculate model center needed for camera focus
      const sizeX = bounds.max.x - bounds.min.x;
      const sizeY = bounds.max.y - bounds.min.y;
      const modelCenterX = bounds.min.x + sizeX / 2; 
      const modelCenterY = bounds.min.y + sizeY / 2;
      
      // Organize paths by layer
      const layerMap = new Map<number, { extrude: THREE.Vector3[][], travel: THREE.Vector3[][] }>();
      
      // Group the paths by layer (z-height)
      toolPaths.forEach(path => {
        if (path.path.length < 2) return;
        
        // Round to 2 decimal places to group similar heights
        const layerZ = Math.round(path.z * 100) / 100;
        
        if (!layerMap.has(layerZ)) {
          layerMap.set(layerZ, { extrude: [], travel: [] });
        }
        
        const layer = layerMap.get(layerZ)!;
        if (path.type === 'extrude') {
          layer.extrude.push(path.path);
        } else {
          layer.travel.push(path.path);
        }
      });
      
      // Sort layers by Z height and filter to only include layers with actual geometry (extrusion paths)
      const allLayers = Array.from(layerMap.keys()).sort((a, b) => a - b);
      const geometryLayers = allLayers.filter(layerZ => {
        const layer = layerMap.get(layerZ)!;
        return layer.extrude.length > 0; // Only include layers that have extrusion paths
      });
      
      console.log(`[DEBUG] Total Z-heights detected: ${allLayers.length}, Layers with geometry: ${geometryLayers.length}`);
      
      // Use geometryLayers instead of sortedLayers for the slider
      const sortedLayers = geometryLayers;
      
      // Generate a color for each layer
      function getLayerColor(layerIndex: number, totalLayers: number): THREE.Color {
        // Use different shades of green as the primary color
        const baseGreen = new THREE.Color(0x07F77F); // Bright green
        
        if (totalLayers <= 1) return baseGreen;
        
        // Determine color based on layer position
        // Lower layers are darker green, higher layers are brighter
        const t = layerIndex / (totalLayers - 1); // 0 to 1
        const intensity = 0.5 + (t * 0.5); // 0.5 to 1.0 for brightness
        
        return new THREE.Color(
          0.1 * intensity, // Very slight red
          0.8 * intensity, // Strong green 
          0.1 * intensity  // Very slight blue
        );
      }
      
      // Create thicker line segments for better visibility
      function createThickLines(
        paths: THREE.Vector3[][],
        color: THREE.Color,
        lineWidth: number = 3
      ): THREE.LineSegments {
        // Create a merged geometry for all paths
        const geometry = new THREE.BufferGeometry();
        const positions: number[] = [];
        const indices: number[] = [];
        
        let vertexCount = 0;
        
        // Process all paths
        paths.forEach(path => {
          if (path.length < 2) return;
          
          // Create line segments
          for (let i = 0; i < path.length - 1; i++) {
            const start = path[i];
            const end = path[i + 1];
            
            // Add vertices for the current segment
            positions.push(start.x, start.y, start.z);
            positions.push(end.x, end.y, end.z);
            
            // Add indices for the current segment
            indices.push(vertexCount, vertexCount + 1);
            vertexCount += 2;
          }
        });
        
        // Set geometry attributes
        geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
        geometry.setIndex(indices);
        
        // Create line material with specified color
        const material = new THREE.LineBasicMaterial({
          color: color,
          linewidth: lineWidth
        });
        
        // Create and return the mesh
        return new THREE.LineSegments(geometry, material);
      }
      
      // Create layer objects
      sortedLayers.forEach((layerZ, index) => {
        const layer = layerMap.get(layerZ)!;
        const layerColor = getLayerColor(index, sortedLayers.length);
        
        // Create line segments for extrusion paths
        if (layer.extrude.length > 0) {
          const extrudeObject = createThickLines(layer.extrude, layerColor, 3);
          extrudeObject.name = `layer_${layerZ}`;
          scene.add(extrudeObject);
        }
      });
      
      // Add travel lines (less visible)
      const allTravelPaths: THREE.Vector3[][] = [];
      layerMap.forEach(layer => {
        allTravelPaths.push(...layer.travel);
      });
      
      if (allTravelPaths.length > 0) {
        // Create material with transparency
        const travelMaterial = new THREE.LineBasicMaterial({
          color: new THREE.Color(0x000000),
          transparent: true,
          opacity: 0.4,
          linewidth: 1
        });
        
        // Create geometry from paths
        const geometry = new THREE.BufferGeometry();
        const positions: number[] = [];
        const indices: number[] = [];
        
        let vertexCount = 0;
        
        // Process all paths
        allTravelPaths.forEach(path => {
          if (path.length < 2) return;
          
          // Create line segments
          for (let i = 0; i < path.length - 1; i++) {
            const start = path[i];
            const end = path[i + 1];
            
            // Add vertices for the current segment
            positions.push(start.x, start.y, start.z);
            positions.push(end.x, end.y, end.z);
            
            // Add indices for the current segment
            indices.push(vertexCount, vertexCount + 1);
            vertexCount += 2;
          }
        });
        
        // Set geometry attributes
        geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
        geometry.setIndex(indices);
        
        const travelObject = new THREE.LineSegments(geometry, travelMaterial);
        travelObject.name = "travelPaths";
        scene.add(travelObject);
      }
      
      console.log('[DEBUG] Found', sortedLayers.length, 'distinct layers');
      console.log('[DEBUG] Model dimensions:', size);
      console.log('[DEBUG] Model center:', modelCenterX, modelCenterY);
      
      onLayerInfoUpdate(sortedLayers.length);
      onSceneRef(scene);
    } catch (error) {
      console.error('[DEBUG] Error parsing GCode:', error);
    }
  }, [gcodeContent, camera, scene, onLayerInfoUpdate, onSceneRef]);
  
  return null; // Don't return OrbitControls here
}

export function GCodeVisualizer({ gcodeContent, width = '100%', height = 300, bedDimensions }: GCodeVisualizerProps) {
  const buildPlateSize = 250;
  const centerX = buildPlateSize/2;
  const centerY = buildPlateSize/2;
  const [currentLayer, setCurrentLayer] = useState<number | null>(null);
  const [maxLayer, setMaxLayer] = useState(0);
  const sceneRef = useRef<THREE.Scene | null>(null);
  const [key, setKey] = useState(0);

  // Update key when gcodeContent changes to remount Canvas and children
  useEffect(() => {
    setKey(prevKey => prevKey + 1); 
  }, [gcodeContent]);

  // Function to control layer visibility
  const handleLayerVisibilityChange = (scene: THREE.Scene, layerNumber: number | null) => {
    if (!scene || layerNumber === null) return;
    
    // Get all layer objects and travel paths
    const layerObjects = scene.children.filter(child => child.name.startsWith("layer_"));
    const travelPaths = scene.getObjectByName("travelPaths") as THREE.LineSegments;
    
    // Get sorted unique layer heights from actual geometry layers only
    const layerZ = layerObjects.map(obj => parseFloat(obj.name.split('_')[1]));
    const sortedGeometryLayers = [...new Set(layerZ)].sort((a, b) => a - b);
    
    // Get the Z height of the current layer
    // Ensure we don't go out of bounds for the last layer
    const currentLayerZ = sortedGeometryLayers[Math.min(layerNumber - 1, sortedGeometryLayers.length - 1)];
    
    // Update visibility of layer objects
    layerObjects.forEach(layer => {
      const thisLayerZ = parseFloat(layer.name.split('_')[1]);
      // Show all geometry layers up to and including the current layer
      layer.visible = thisLayerZ <= currentLayerZ;
      
      // Special case: when on last layer, ensure all layers are visible
      if (layerNumber >= sortedGeometryLayers.length) {
        layer.visible = true;
      }
    });

    // Update travel paths visibility
    if (travelPaths) {
      travelPaths.visible = true;
      const material = travelPaths.material as THREE.LineBasicMaterial;
      if (material) {
        material.opacity = 0.4;
        material.transparent = true;
        material.needsUpdate = true;
      }
    }
    
    // No camera adjustments here - preserving user's current view
  };

  // Pass the scene reference up from the child component
  const handleSceneRef = (scene: THREE.Scene) => {
    // Store the scene reference
    sceneRef.current = scene;
    
    // Check if the scene already has layer objects
    const hasLayerObjects = scene.children.some(child => child.name.startsWith("layer_"));
    
    // Only initialize layer visibility for first render, not when changing layers
    if (currentLayer !== null && !hasLayerObjects) {
      handleLayerVisibilityChange(scene, currentLayer);
    } else if (currentLayer !== null) {
      // For subsequent updates, just update layer visibility without camera changes
      handleLayerVisibilityChange(scene, currentLayer);
    }
  };

  // Effect to update layer visibility when controls change
  useEffect(() => {
    if (sceneRef.current && currentLayer !== null) {
      // Only update layer visibility without affecting camera position
      handleLayerVisibilityChange(sceneRef.current, currentLayer);
    }
  }, [currentLayer]);

  // Function to share layer info from inner component
  const handleLayerInfoUpdate = (layerCount: number) => {
    setMaxLayer(layerCount);
    if (currentLayer === null) {
      setCurrentLayer(layerCount); // Start with the last layer visible
    }
  };

  if (!gcodeContent) {
    return (
      <div 
        style={{ 
          width, 
          height, 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          backgroundColor: '#ffffff' 
        }}
      >
        <p style={{ color: '#888888' }}>No G-code content available</p>
      </div>
    );
  }
  
  return (
    <div style={{ width, height, position: 'relative' }}>
      <style>
        {`
          .layer-slider::-webkit-slider-thumb {
            appearance: none;
            width: 16px;
            height: 16px;
            border-radius: 50%;
            background: #07F77F;
            cursor: pointer;
            border: none;
          }
          .layer-slider::-moz-range-thumb {
            width: 16px;
            height: 16px;
            border-radius: 50%;
            background: #07F77F;
            cursor: pointer;
            border: none;
          }
        `}
      </style>
      <Canvas 
        key={key} 
        style={{ background: '#ffffff' }}
        camera={{
          position: [125, -125, 125], // 45-degree angle
          up: [0, 0, 1],
          far: 2000,
          near: 0.1,
          fov: 45
        }}
        gl={{ preserveDrawingBuffer: true }}
        onCreated={({ gl, camera, scene }) => {
          // Store initial camera state when Canvas is created
          scene.userData.initialCameraPosition = camera.position.clone();
          scene.userData.initialCameraRotation = camera.rotation.clone();
        }}
      >
        <ambientLight intensity={0.6} />
        <directionalLight position={[8, 5, 10]} intensity={0.8} />
        <GCodeToolPaths 
          gcodeContent={gcodeContent} 
          onLayerInfoUpdate={handleLayerInfoUpdate}
          onSceneRef={handleSceneRef}
          bedDimensions={bedDimensions}
        />
        <OrbitControls 
          makeDefault
          target={[125, 125, 0]}
          enableDamping={true}
          dampingFactor={0.05}
          minDistance={50}
          maxDistance={1000}
          maxPolarAngle={Math.PI / 2}
          up={[0, 0, 1]}
        />
      </Canvas>
      
      {maxLayer > 0 && (
        <div 
          style={{ 
            position: 'absolute', 
            bottom: '10px', 
            left: '10px', 
            right: '10px', 
            background: '#E7E7E7', 
            padding: '10px',
            borderRadius: '6px',
            display: 'flex',
            flexDirection: 'column',
            gap: '8px'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <input
              type="range"
              min="1"
              max={maxLayer}
              value={currentLayer || maxLayer}
              onChange={(e) => setCurrentLayer(parseInt(e.target.value))}
              className="layer-slider"
              style={{ 
                flex: 1,
                borderRadius: '12px',
                height: '6px',
                outline: 'none',
                appearance: 'none',
                background: '#CCCCCC'
              }}
            />
            <span style={{ color: '#888888', minWidth: '60px', textAlign: 'right', fontSize: '12px' }}>
              Layer {currentLayer || maxLayer}/{maxLayer}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}